import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Award, TrendingDown, MapPin, Check } from 'lucide-react';

// /shingles — "Explore Shingle Options" section, rebuilt 2026-05-26
// from the user's mockup screenshots to replace the prior animated
// version (gradient orbs, color-swatch picker, click-to-enlarge
// modal, "Ready to See Your Full Roof Price?" CTA card). Five
// product cards in a 3-col grid, mockup-faithful badges/chips/copy.
//
// File still lives under /pricing/components because /shingles
// imports it from there — left untouched to keep that import path
// stable. The /pricing page itself was unrendered earlier; this
// component now only renders on /shingles.
//
// Atlas Pinnacle Pristine roof photo: not yet available. The card
// uses a styled placeholder (Atlas logo centered on a navy panel).
// Replace by adding the path to the SHINGLE_CARDS[4].photo field
// once a real photo is in /public/assets/images/.

const EASE_OUT_EXPO = [0.16, 1, 0.3, 1];

const CHIP_STYLES = {
  green: { bg: '#d1fae5', color: '#047857', border: '1px solid #a7f3d0' },
  blue:  { bg: '#dbeafe', color: '#0a6eb8', border: '1px solid #bfdbfe' },
  pink:  { bg: '#ffe4e6', color: '#9f1239', border: '1px solid #fecdd3' },
};

const TRUST_INDICATORS = [
  { label: 'Warranty Protected', Icon: Shield, color: '#10B981' },
  { label: 'Premium Brands', Icon: Award, color: '#F59E0B' },
  { label: 'No Markups', Icon: TrendingDown, color: '#8B5CF6' },
];

const SHINGLE_CARDS = [
  {
    id: 'tamko-titan-xt',
    brand: 'TAMKO',
    productName: 'Titan XT',
    badge: 'Best Value',
    featured: true, // cyan border per mockup
    colorName: 'Rustic Black',
    photo: '/assets/images/tamko-titan-xt-rustic-black-house_1_-1768340144049.jpg',
    photoAlt: 'TAMKO Titan XT shingles in Rustic Black installed on residential home',
    warranty: 'Lifetime Warranty – 50 Year Non-Prorated',
    logoSrc: '/assets/images/TAMKO_3D_logo-1767901576132.jpg',
    price: 118,
    chips: [
      { label: 'Transferable once', variant: 'green', check: true },
      { label: 'Up to 160 mph wind', variant: 'blue' },
      { label: '10-yr algae cleaning', variant: 'pink' },
    ],
  },
  {
    id: 'gaf-timberline-hdz',
    brand: 'GAF',
    productName: 'Timberline HDZ',
    badge: 'Most Popular',
    featured: false,
    colorName: 'Charcoal',
    photo: '/assets/images/gaf-timberline-hdz-charcoal-house_1_-1768332752421.jpg',
    photoAlt: 'GAF Timberline HDZ shingles in Charcoal installed on residential home',
    warranty: 'Lifetime Warranty – 10 Year Non-Prorated',
    logoSrc: '/assets/images/GAF_3D_logo_1_-1767901680135.jpg',
    price: 125,
    chips: [
      { label: 'Transferable once', variant: 'green', check: true },
      { label: 'Up to 130 mph wind', variant: 'blue' },
      { label: '25-yr StainGuard Plus', variant: 'pink' },
    ],
  },
  {
    id: 'owens-corning-duration',
    brand: 'Owens Corning',
    productName: 'Duration',
    badge: 'Premium Choice',
    featured: false,
    colorName: 'Black Sable',
    photo: '/assets/images/Black_sable-1768337652778.jpg',
    photoAlt: 'Owens Corning Duration shingles in Black Sable installed on residential home',
    warranty: 'Lifetime Warranty – 10 Year Non-Prorated',
    logoSrc: '/assets/images/Owens_Corning_3D_logo_1_-1767901713667.jpg',
    price: 134,
    chips: [
      { label: 'Transferable once', variant: 'green', check: true },
      { label: 'Up to 130 mph wind', variant: 'blue' },
      { label: '25-yr StreakGuard', variant: 'pink' },
    ],
  },
  {
    id: 'owens-corning-oakridge',
    brand: 'Owens Corning',
    productName: 'Oakridge',
    badge: null,
    featured: false,
    colorName: 'Onyx Black',
    photo: '/assets/images/owens-corning-oakridge-shingles-onyx-black-house_1_-1768334487021.jpg',
    photoAlt: 'Owens Corning Oakridge shingles in Onyx Black installed on residential home',
    warranty: 'Lifetime Warranty – 10 Year Non-Prorated',
    logoSrc: '/assets/images/Owens_Corning_3D_logo_1_-1767901713667.jpg',
    price: 122,
    chips: [
      { label: 'Transferable once', variant: 'green', check: true },
      { label: 'Up to 130 mph wind', variant: 'blue' },
      { label: '25-yr StreakGuard', variant: 'pink' },
    ],
  },
  {
    id: 'atlas-pinnacle-pristine',
    brand: 'Atlas',
    productName: 'Pinnacle Pristine',
    badge: 'Class 3 Hail Impact',
    featured: false,
    colorName: 'Garnet Black',
    photo: null, // no Atlas roof photo yet — styled placeholder rendered below
    photoAlt: 'Atlas Pinnacle Pristine shingles in Garnet Black (placeholder — real photo pending)',
    warranty: 'Lifetime Warranty – 10 Year Non-Prorated',
    logoSrc: '/assets/images/atlas-logo.webp',
    price: 120,
    chips: [
      { label: 'Transferable once', variant: 'green', check: true },
      { label: 'Up to 130 mph wind', variant: 'blue' },
      { label: 'Lifetime Scotchgard', variant: 'pink' },
    ],
  },
];

const Chip = ({ chip }) => {
  const s = CHIP_STYLES[chip.variant] || CHIP_STYLES.blue;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap"
      style={{ background: s.bg, color: s.color, border: s.border }}
    >
      {chip.check && <Check size={12} strokeWidth={3} aria-hidden="true" />}
      {chip.label}
    </span>
  );
};

const PhotoOrPlaceholder = ({ card }) => {
  if (card.photo) {
    return (
      <img
        src={card.photo}
        alt={card.photoAlt}
        className="absolute inset-0 w-full h-full object-cover"
        loading="lazy"
      />
    );
  }
  // Atlas placeholder — styled navy panel with the logo centered.
  return (
    <div
      className="absolute inset-0 flex items-center justify-center"
      style={{
        background: 'linear-gradient(135deg, #1e293b 0%, #0a1628 100%)',
      }}
      aria-label={card.photoAlt}
    >
      <div
        className="rounded-2xl p-5 flex items-center justify-center"
        style={{
          background: 'rgba(255, 255, 255, 0.10)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          border: '1px solid rgba(255, 255, 255, 0.18)',
        }}
      >
        <img
          src={card.logoSrc}
          alt=""
          className="h-14 w-auto object-contain"
          style={{ filter: 'brightness(1.1)' }}
        />
      </div>
    </div>
  );
};

const ShingleCard = ({ card, index }) => (
  <motion.article
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.55, delay: 0.05 + index * 0.06, ease: EASE_OUT_EXPO }}
    whileHover={{ y: -4, transition: { duration: 0.25, ease: 'easeOut' } }}
    className="relative rounded-2xl bg-white overflow-hidden flex flex-col"
    style={{
      border: card.featured ? '2px solid #07B0E9' : '1px solid #e5ebf0',
      boxShadow: card.featured
        ? '0 12px 32px rgba(7, 176, 233, 0.12), 0 0 0 4px rgba(7, 176, 233, 0.04)'
        : '0 1px 3px rgba(15, 23, 42, 0.04), 0 8px 24px rgba(15, 23, 42, 0.06)',
    }}
  >
    {/* Photo / placeholder area with overlay badge + color name */}
    <div className="relative aspect-[16/10] overflow-hidden" style={{ background: '#e5ebf0' }}>
      <PhotoOrPlaceholder card={card} />

      {/* Featured badge — cyan pill, top-left */}
      {card.badge && (
        <div className="absolute top-4 left-4">
          <span
            className="inline-block px-3 py-1 rounded-full text-[11px] font-bold text-white"
            style={{
              background: '#07B0E9',
              boxShadow: '0 4px 12px rgba(7, 176, 233, 0.30)',
            }}
          >
            {card.badge}
          </span>
        </div>
      )}

      {/* Color name pill — white, top-right */}
      <div className="absolute top-4 right-4">
        <span
          className="inline-block px-3 py-1 rounded-full text-[11px] font-bold"
          style={{
            background: 'white',
            color: '#0a1628',
            boxShadow: '0 4px 12px rgba(15, 23, 42, 0.10)',
          }}
        >
          {card.colorName}
        </span>
      </div>
    </div>

    {/* Warranty bar — dark navy, shield + text */}
    <div
      className="flex items-center gap-2 px-5 py-3"
      style={{ background: '#0a1628' }}
    >
      <Shield size={14} strokeWidth={2.4} color="#6cc7ff" aria-hidden="true" />
      <span className="text-[12.5px] font-bold text-white">
        {card.warranty}
      </span>
    </div>

    {/* Card body */}
    <div className="p-6 lg:p-7 flex flex-col flex-1">
      {/* Brand row — logo + caps brand name */}
      <div className="flex items-center gap-3 mb-1">
        <img
          src={card.logoSrc}
          alt={`${card.brand} logo`}
          className="flex-shrink-0 rounded-md object-contain bg-white"
          style={{ width: '36px', height: '36px' }}
          loading="lazy"
        />
        <div
          className="text-[12px] font-bold uppercase tracking-[0.14em]"
          style={{ color: '#07B0E9' }}
        >
          {card.brand}
        </div>
      </div>

      {/* Product name */}
      <h3
        className="font-black leading-tight mb-3"
        style={{
          fontSize: 'clamp(22px, 1.8vw, 26px)',
          color: '#0a1628',
          fontFamily: "'Poppins', 'Montserrat', sans-serif",
          letterSpacing: '-0.015em',
        }}
      >
        {card.productName}
      </h3>

      {/* Price */}
      <div className="flex items-baseline gap-2 mb-4">
        <span
          className="font-black leading-none"
          style={{
            fontSize: 'clamp(32px, 3.2vw, 40px)',
            color: '#07B0E9',
            letterSpacing: '-0.02em',
          }}
        >
          ${card.price}
        </span>
        <span className="text-sm font-medium text-gray-500">per square</span>
      </div>

      {/* Info row — pin icon + helper */}
      <div
        className="flex items-start gap-2 rounded-lg px-3 py-2 mb-5"
        style={{ background: '#F1F5F9' }}
      >
        <MapPin size={14} strokeWidth={2.2} className="flex-shrink-0 mt-0.5" style={{ color: '#07B0E9' }} aria-hidden="true" />
        <span className="text-xs lg:text-[13px] text-gray-600 leading-snug">
          Price is per square for shingles only; accessories apply.
        </span>
      </div>

      {/* Chips — 3 pills, wrap on narrow widths */}
      <div className="flex flex-wrap gap-2 mt-auto">
        {card.chips.map((chip, i) => (
          <Chip key={`${chip.label}-${i}`} chip={chip} />
        ))}
      </div>
    </div>
  </motion.article>
);

const ShinglePricingSection = () => (
  <section className="py-16 lg:py-24" style={{ background: '#F8FAFC' }}>
    <div className="max-w-[1280px] mx-auto px-5 lg:px-8">
      {/* Section header */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={{ hidden: {}, visible: {} }}
        className="text-center mb-12 lg:mb-16"
      >
        {/* Eyebrow pill */}
        <motion.div
          variants={{
            hidden: { opacity: 0, y: 8 },
            visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: EASE_OUT_EXPO } },
          }}
          className="mb-5 flex justify-center"
        >
          <div
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.14em]"
            style={{
              background: 'rgba(7, 176, 233, 0.10)',
              border: '1px solid rgba(7, 176, 233, 0.30)',
              color: '#0a6eb8',
            }}
          >
            <span
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: '#10B981' }}
              aria-hidden="true"
            />
            Material Transparency
          </div>
        </motion.div>

        {/* Headline */}
        <motion.h2
          variants={{
            hidden: { opacity: 0, y: 16 },
            visible: { opacity: 1, y: 0, transition: { duration: 0.55, delay: 0.08, ease: EASE_OUT_EXPO } },
          }}
          className="font-black leading-[1.05] tracking-tight mb-4 mx-auto"
          style={{
            fontSize: 'clamp(34px, 4.8vw, 60px)',
            fontFamily: "'Poppins', 'Montserrat', sans-serif",
            letterSpacing: '-0.025em',
            color: '#0a1628',
            maxWidth: '880px',
          }}
        >
          Explore <span style={{ color: '#07B0E9' }}>Shingle Options</span>
        </motion.h2>

        {/* Subhead */}
        <motion.p
          variants={{
            hidden: { opacity: 0, y: 12 },
            visible: { opacity: 1, y: 0, transition: { duration: 0.5, delay: 0.18, ease: EASE_OUT_EXPO } },
          }}
          className="text-base lg:text-lg text-gray-500 max-w-2xl mx-auto leading-relaxed mb-7"
        >
          Transparent pricing on quality roofing materials. See exactly what you're paying for — no markups, no hidden fees.
        </motion.p>

        {/* 3 trust chips */}
        <motion.div
          variants={{
            hidden: { opacity: 0, y: 8 },
            visible: { opacity: 1, y: 0, transition: { duration: 0.5, delay: 0.28, ease: EASE_OUT_EXPO } },
          }}
          className="flex flex-wrap justify-center gap-3"
        >
          {TRUST_INDICATORS.map(({ label, Icon: TrustIcon, color }) => (
            <div
              key={label}
              className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full"
              style={{
                border: '1px solid #e5ebf0',
                boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
              }}
            >
              <TrustIcon size={16} strokeWidth={2.2} style={{ color }} aria-hidden="true" />
              <span className="text-sm font-semibold text-gray-800">{label}</span>
            </div>
          ))}
        </motion.div>
      </motion.div>

      {/* Card grid — 3-col on lg+, 2-col on md, 1-col on mobile.
          With 5 cards the last row has 2 cards on lg (slots 1+2 of 3,
          slot 3 empty — natural grid flow). */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7">
        {SHINGLE_CARDS.map((card, i) => (
          <ShingleCard key={card.id} card={card} index={i} />
        ))}
      </div>
    </div>
  </section>
);

export default ShinglePricingSection;
